import React from 'react';
import PropTypes from 'prop-types';
import {Dropdown, Button} from '@/components/atoms';
import styles from '../RewardSetup.module.scss';
import BonusProductsCondition from './BonusProductsCondition';
import DigitalCondition from './DigitalCondition';
import ProductOrPepVoucherCondition from './ProductOrPepVoucherCondition';
import VisaCard from './RewardsCondition/VisaCard/VisaCard';
import PointsCondition from './PointsCondition';
import LoyaltyUrl from './LoyaltyUrl';
import RewardPreview from './RewardPreview';
import CashDiscountCondition from './CashDiscountCondition';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';

/**
 * RewardConditionsCard component displays the reward conditions section
 * Contains reward type dropdown, points input, and reset button
 * For BONUS_PRODUCTS reward type, shows different structure with product selection modal, order quantity, and limit
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing rewardType, points, selectedProducts, orderQty, limitOfProductQty
 * @param {Object} props.validation - Validation state object
 * @param {Array} props.rewardTypes - Array of reward type options
 * @param {boolean} props.rewardTypesLoading - Loading state for reward types
 * @param {Function} props.handleRewardTypeChange - Handler for reward type changes
 * @param {Function} props.handlePointsChange - Handler for points input changes
 * @param {Function} props.handleProductsChange - Handler for products selection changes
 * @param {Function} props.handleOrderQtyChange - Handler for order quantity changes
 * @param {Function} props.handleLimitOfProductQtyChange - Handler for limit of product qty changes
 * @param {Function} props.handleLoyaltyUrlChange - Handler for loyalty URL input changes
 * @param {Function} props.resetRewardConditions - Handler for reset button
 * @param {string} props.pointsError - Error message for points validation
 * @param {string} props.orderQtyError - Error message for order quantity validation
 * @param {string} props.limitOfProductQtyError - Error message for limit of product qty validation
 * @param {string} props.loyaltyUrlError - Error message for loyalty URL validation
 * @returns {JSX.Element} The rendered RewardConditionsCard component
 */
const RewardConditionsCard = ({
  formData,
  validation,
  rewardTypes,
  rewardTypesLoading,
  rewardCardTypes,
  rewardCardTypesLoading,
  bonusProductsLoading,
  pepVouchersOrProductsLoading,
  handleRewardTypeChange,
  handlePointsChange,
  handleProductsChange,
  handleOrderQtyChange,
  handleLimitOfProductQtyChange,
  handleCardTypeChange,
  handleRedeemPointsChange,
  handleRewardCashAmountChange,
  // Digital specific
  handleDigitalCodeAmountChange,
  handleDigitalCodePartnerChange,
  handlePepVouchersChange,
  handleValueChange,
  handleLoyaltyUrlChange,
  handleCouponBasedLoyaltyRewardChange,
  resetRewardConditions,
  pointsError,
  orderQtyError,
  limitOfProductQtyError,
  rewardCashAmountError,
  redeemPointsError,
  cardTypeError,
  valueError,
  loyaltyUrlError,
  // Digital specific errors
  digitalCodeAmountError,
  digitalCodePartnerError,
  isRewardTypeDisabled = false
}) => {
  // Function to render the appropriate condition component based on reward type
  const renderConditionComponent = () => {
    switch (formData.rewardType) {
      case REWARD_TYPES.BONUS_PRODUCTS:
        return (
          <BonusProductsCondition
            formData={formData}
            validation={validation}
            bonusProductsLoading={bonusProductsLoading}
            handleProductsChange={handleProductsChange}
            handleOrderQtyChange={handleOrderQtyChange}
            handleLimitOfProductQtyChange={handleLimitOfProductQtyChange}
            orderQtyError={orderQtyError}
            limitOfProductQtyError={limitOfProductQtyError}
          />
        );
      case REWARD_TYPES.DIGITAL:
      case REWARD_TYPES.DIGITAL_PRE_ORDER:
        return (
          <DigitalCondition
            formData={formData}
            handleDigitalCodeAmountChange={handleDigitalCodeAmountChange}
            handleDigitalCodePartnerChange={handleDigitalCodePartnerChange}
            handleRedeemPointsChange={handleRedeemPointsChange}
            digitalCodeAmountError={digitalCodeAmountError}
            redeemPointsError={redeemPointsError}
            digitalCodePartnerError={digitalCodePartnerError}
          />
        );
      case REWARD_TYPES.PRODUCT:
        return (
          <ProductOrPepVoucherCondition
            formData={formData}
            validation={validation}
            pepVouchersOrProductsLoading={pepVouchersOrProductsLoading}
            handleProductsChange={handleProductsChange}
            handlePointsChange={handlePointsChange}
            pointsError={pointsError}
          />
        );
      case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
        return (
          <ProductOrPepVoucherCondition
            formData={formData}
            validation={validation}
            pepVouchersOrProductsLoading={pepVouchersOrProductsLoading}
            handlePepVouchersChange={handlePepVouchersChange}
            handlePointsChange={handlePointsChange}
            pointsError={pointsError}
          />
        );

      case REWARD_TYPES.VISA_CARD:
        return (
          <VisaCard
            formData={formData}
            validation={validation}
            rewardCardTypes={rewardCardTypes}
            rewardCardTypesLoading={rewardCardTypesLoading}
            handleRedeemPointsChange={handleRedeemPointsChange}
            handleCardTypeChange={handleCardTypeChange}
            handleRewardCashAmountChange={handleRewardCashAmountChange}
            redeemPointsError={redeemPointsError}
            cardTypeError={cardTypeError}
            rewardCashAmountError={rewardCashAmountError}
          />
        );

      case REWARD_TYPES.CASH:
      case REWARD_TYPES.DISCOUNT:
      case REWARD_TYPES.PEP_VOUCHER_DISCOUNT:
        return (
          <CashDiscountCondition
            formData={formData}
            handleValueChange={handleValueChange}
            handlePointsChange={handlePointsChange}
            valueError={valueError}
            pointsError={pointsError}
          />
        );

      default:
        return (
          <PointsCondition
            type='text'
            maxLength={8}
            formData={formData}
            handlePointsChange={handlePointsChange}
            pointsError={pointsError}
          />
        );
    }
  };

  // Render helper for LoyaltyUrl so it's easier to reuse and test
  const renderLoyaltyUrl = () => {
    if (formData?.rewardType !== REWARD_TYPES.LOYALTY) return null;

    return (
      <LoyaltyUrl
        formData={formData}
        handleLoyaltyUrlChange={handleLoyaltyUrlChange}
        handleCouponBasedLoyaltyRewardChange={
          handleCouponBasedLoyaltyRewardChange
        }
        loyaltyUrlError={loyaltyUrlError}
      />
    );
  };

  return (
    <div
      className={`flex flex-col gap-6 rounded-lg p-6 ${styles.rewardSetup__card}`}>
      <div className='flex items-center justify-between'>
        <h3 className='text-base font-bold text-white'>Reward Conditions*</h3>
        <div className='flex justify-end'>
          {!formData.rewardId && !formData.isDuplicateFlow && (
            <Button
              handleButton={resetRewardConditions}
              uiSchema={{
                title: 'Reset',
                variant: 'secondary',
                size: 'small'
              }}
              style={{
                color: '#66A5E7',
                borderColor: '#66A5E7'
              }}
            />
          )}
        </div>
      </div>
      <div className='flex flex-col'>
        {/* Reward Type Selection */}
        <div className='flex flex-wrap gap-[13px] text-white'>
          <span className='mt-2 whitespace-nowrap text-sm'>Get</span>
          <div className={`${styles.rewardSetup__dropdownWrapper}`}>
            <Dropdown
              className={`text-sm placeholder:text-sm ${styles.rewardSetup__rewardType}`}
              value={
                formData.rewardType
                  ? rewardTypes.filter((rt) => rt.value === formData.rewardType)
                  : []
              }
              updateValue={handleRewardTypeChange}
              isError={validation.rewardTypeValid}
              errorMessage={
                validation.rewardTypeValid ? 'Reward type is required' : ''
              }
              isDark={true}
              childList={rewardTypes}
              placeholder={
                rewardTypesLoading ? 'Loading...' : 'Select Reward Type'
              }
              isJsonWrap={false}
              selection='single'
              style={{width: '220px'}}
              hideMenuListOnSelection={true}
              isClearable={false}
              disabled={!!formData.rewardId || isRewardTypeDisabled}
            />
          </div>

          {/* Render appropriate condition component */}
          {renderConditionComponent()}
        </div>

        {/* Reward Preview */}
        <RewardPreview formData={formData} />

        {/* Loyalty URL field - shown after preview for LOYALTY reward type */}
        {renderLoyaltyUrl()}
      </div>
    </div>
  );
};

RewardConditionsCard.propTypes = {
  formData: PropTypes.shape({
    rewardType: PropTypes.string,
    points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedProducts: PropTypes.array,
    orderQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    limitOfProductQty: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    rewardId: PropTypes.string,
    isDuplicateFlow: PropTypes.bool
  }).isRequired,
  validation: PropTypes.shape({
    rewardTypeValid: PropTypes.bool
  }).isRequired,
  rewardTypes: PropTypes.array.isRequired,
  rewardTypesLoading: PropTypes.bool,
  rewardCardTypes: PropTypes.array,
  rewardCardTypesLoading: PropTypes.bool,
  bonusProductsLoading: PropTypes.bool,
  pepVouchersOrProductsLoading: PropTypes.bool,
  handleRewardTypeChange: PropTypes.func.isRequired,
  handlePointsChange: PropTypes.func.isRequired,
  handleProductsChange: PropTypes.func.isRequired,
  handleOrderQtyChange: PropTypes.func.isRequired,
  handleLimitOfProductQtyChange: PropTypes.func.isRequired,
  handleCardTypeChange: PropTypes.func,
  handleRedeemPointsChange: PropTypes.func,
  handleRewardCashAmountChange: PropTypes.func,
  handleDigitalCodeAmountChange: PropTypes.func,
  handleDigitalCodePartnerChange: PropTypes.func,
  handlePepVouchersChange: PropTypes.func,
  handleValueChange: PropTypes.func,
  handleLoyaltyUrlChange: PropTypes.func,
  handleCouponBasedLoyaltyRewardChange: PropTypes.func,
  resetRewardConditions: PropTypes.func.isRequired,
  pointsError: PropTypes.string,
  orderQtyError: PropTypes.string,
  limitOfProductQtyError: PropTypes.string,
  rewardCashAmountError: PropTypes.string,
  redeemPointsError: PropTypes.string,
  cardTypeError: PropTypes.string,
  valueError: PropTypes.string,
  loyaltyUrlError: PropTypes.string,
  digitalCodeAmountError: PropTypes.string,
  digitalCodePartnerError: PropTypes.string,
  isRewardTypeDisabled: PropTypes.bool
};

export default React.memo(RewardConditionsCard);
